import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Button } from '../../components/ui/Button';
import { PenTool, BookOpen, Users, Target } from 'lucide-react';
export function AboutPublication() {
  const editors = [{
    name: 'Sam Chen',
    role: 'Editor-in-Chief',
    bio: 'Sam is a fat writer and cultural critic whose work has appeared in The New Inquiry, Catapult, and elsewhere. They bring 10 years of editorial experience and a commitment to centering marginalized voices.'
  }, {
    name: 'Jordan Martinez',
    role: 'Managing Editor',
    bio: 'Jordan is a fat disabled poet and essayist. They oversee day-to-day operations and work closely with contributors to develop their pieces.'
  }, {
    name: 'Alex Thompson',
    role: 'Poetry Editor',
    bio: 'Alex is a fat trans poet whose work explores embodiment, desire, and resistance. They curate our poetry section and run our annual poetry prize.'
  }, {
    name: 'Riley Johnson',
    role: 'Art Director',
    bio: 'Riley is a fat visual artist and designer. They commission illustrations, photography, and visual essays that challenge dominant narratives about bodies.'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-449">
      <HeroSection title="About The Publication" subtitle="A journal of fat thought, art, and politics." tag="The Digital Publication" bgColor="orange" data-id="element-450" />

      {/* Mission Statement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-451">
        <div className="text-center mb-16" data-id="element-452">
          <span className="text-brand-orange font-bold tracking-widest uppercase mb-4 block text-sm" data-id="element-453">
            Our Mission
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-medium text-brand-black mb-8 leading-tight" data-id="element-454">
            "We publish work that challenges, expands, and deepens the
            conversation around fat justice."
          </h2>
        </div>

        <div className="prose prose-lg max-w-none" data-id="element-455">
          <p className="text-gray-700 leading-relaxed mb-6" data-id="element-456">
            The Digital Publication is not a blog. It's not a magazine. It's a{' '}
            <strong className="text-brand-orange" data-id="element-457">
              platform for fat people to talk to each other
            </strong>
            —to share ideas, debate strategy, process grief, celebrate joy, and
            imagine futures.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6" data-id="element-458">
            We are not interested in 101-level explainers for thin audiences. We
            assume our readers already understand that fatphobia is real,
            systemic, and deadly. We're here to go deeper.
          </p>
          <p className="text-gray-700 leading-relaxed" data-id="element-459">
            We publish essays, reporting, poetry, visual art, and hybrid forms.
            We pay our contributors. We take our time. We value craft.
          </p>
        </div>
      </section>

      {/* What We Publish */}
      <section className="py-20 bg-white" data-id="element-460">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-461">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-462">
            What We Publish
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-463">
            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-464">
              <PenTool className="h-10 w-10 text-brand-orange mb-4" data-id="element-465" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-466">
                Essays & Criticism
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-467">
                Long-form essays that analyze culture, politics, and lived
                experience. We love theory, but we also love storytelling.
              </p>
              <p className="text-sm text-gray-600" data-id="element-468">
                Recent: "The Architecture of Exclusion," "Hunger as Resistance,"
                "Beyond Representation"
              </p>
            </div>

            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-469">
              <BookOpen className="h-10 w-10 text-brand-orange mb-4" data-id="element-470" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-471">
                Reporting & Investigations
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-472">
                Investigative pieces that expose systems of oppression and hold
                institutions accountable.
              </p>
              <p className="text-sm text-gray-600" data-id="element-473">
                Recent: "Inside the Bariatric Surgery Industry," "How Schools
                Weaponize BMI," "The Cost of Being Fat"
              </p>
            </div>

            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-474">
              <Users className="h-10 w-10 text-brand-orange mb-4" data-id="element-475" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-476">
                Poetry & Creative Work
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-477">
                Poems, hybrid forms, and experimental writing that explore
                fatness, embodiment, and liberation.
              </p>
              <p className="text-sm text-gray-600" data-id="element-478">
                Recent: "Body as Archive," "Ode to My Belly," "Inheritance: A
                Fat Daughter's Reckoning"
              </p>
            </div>

            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-479">
              <Target className="h-10 w-10 text-brand-orange mb-4" data-id="element-480" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-481">
                Visual Essays & Art
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-482">
                Photography, illustration, comics, and visual storytelling that
                centers fat bodies and fat joy.
              </p>
              <p className="text-sm text-gray-600" data-id="element-483">
                Recent: "Fat Futures" photo series, "Reclaiming Space"
                illustration project
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Team */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-484">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-485">
          Editorial Team
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-486">
          {editors.map((editor, index) => <div key={index} className="flex gap-6" data-id="element-487">
              <div className="w-24 h-24 rounded-full bg-brand-orange flex-shrink-0 border-2 border-brand-black" data-id="element-488"></div>
              <div data-id="element-489">
                <h3 className="text-xl font-bold text-brand-black mb-1" data-id="element-490">
                  {editor.name}
                </h3>
                <p className="text-brand-orange font-bold uppercase text-sm mb-3" data-id="element-491">
                  {editor.role}
                </p>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-492">
                  {editor.bio}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Editorial Values */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-493">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-494">
          <h2 className="text-3xl font-black uppercase mb-12 text-center" data-id="element-495">
            Our Editorial Values
          </h2>

          <div className="space-y-8" data-id="element-496">
            <div className="border-l-4 border-brand-orange pl-6" data-id="element-497">
              <h3 className="text-xl font-bold mb-2" data-id="element-498">
                We Pay Our Contributors
              </h3>
              <p className="text-gray-300" data-id="element-499">
                We believe in compensating creative and intellectual labor. Our
                rates start at $0.25/word for essays and $100 for poems.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange pl-6" data-id="element-500">
              <h3 className="text-xl font-bold mb-2" data-id="element-501">We Take Our Time</h3>
              <p className="text-gray-300" data-id="element-502">
                We don't chase virality or publish on a rigid schedule. We work
                with writers to develop their pieces over weeks or months.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange pl-6" data-id="element-503">
              <h3 className="text-xl font-bold mb-2" data-id="element-504">We Center Fat Voices</h3>
              <p className="text-gray-300" data-id="element-505">
                All our contributors are fat. We don't publish thin people
                writing about their "body image journey" or their fat friends.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange pl-6" data-id="element-506">
              <h3 className="text-xl font-bold mb-2" data-id="element-507">We Value Craft</h3>
              <p className="text-gray-300" data-id="element-508">
                We care about good writing. We work with contributors on
                structure, language, and form. We believe politics and craft are
                inseparable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 text-center" data-id="element-509">
        <div className="max-w-3xl mx-auto" data-id="element-510">
          <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-511">
            Read The Publication
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-512">
            Visit our dedicated editorial platform to read all our published
            work.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-513">
            <Button color="orange" size="lg" withArrow data-id="element-514">
              Go to Publication
            </Button>
            <Button variant="outline" color="orange" size="lg" data-id="element-515">
              Submit Your Work
            </Button>
          </div>
        </div>
      </section>
    </div>;
}